import { CloseOutlined, DeleteOutlined, EditOutlined } from '@ant-design/icons'
import { Button, Input, Modal, Table } from 'antd'
import Divider from 'antd/lib/divider'
import { debounce } from 'lodash'
import * as React from 'react'
import { useEffect, useState } from 'react'
import Highlighter from 'react-highlight-words'
import { Link } from 'react-router-dom'
import { HouseCreativeBean, ItemResponseHouseCreativeBeanObject, ListPagination, ListResponseHouseCreativeBean } from 'src/api/api'
import { filterFunc, getFilterData, sorterFunc, withActionsMediaQuery } from 'src/util/Util'
import { StorageKey, storageReaderFactory, storageWriterFactory } from '../../../util/Storage'
import houseCreativeApi from './HouseCreativeApi'
import './style.css'

const Column = Table.Column

const storageWriter = storageWriterFactory('HouseCreative')
const storageReader = storageReaderFactory('HouseCreative')

interface Props {
    areActionsVertical?: boolean
}

const HouseCreativeList:React.FC<Props> = ({areActionsVertical}) => {

    const [data, setData] = useState<HouseCreativeBean[]>([])
    const [pagination, setPagination] = useState<{
        defaultCurrent: number,
        defaultPageSize: number,
        current: number
    }>({
        defaultCurrent: 1,
        defaultPageSize: 10,
        current: 1
    })
    const [searchQuery, setSearchQuery] = useState('')

    useEffect(() => {
        let searchParams = storageReader.read(StorageKey.SearchParams)
        let page = searchParams.page || pagination.current

        loadData(page)
    }, [])

    const loadData = (page) => {
        houseCreativeApi
            .list(pagination.defaultPageSize, page, searchQuery)
            .then((listResponseHouseCreativeBean: ListResponseHouseCreativeBean) => {

                const array = listResponseHouseCreativeBean.data as Array<ItemResponseHouseCreativeBeanObject>
                const HouseCreativeBeans: HouseCreativeBean[] = array.map(item => item.data as HouseCreativeBean)

                if (HouseCreativeBeans.length == 0 && page > 1) {
                    loadData(page - 1)
                } else {
                    const newPagination: ListPagination = listResponseHouseCreativeBean.pagination as ListPagination
                    storageWriter.write(StorageKey.SearchParams, {
                        page: newPagination.currentPage
                    })

                    setData(HouseCreativeBeans)
                    setPagination(p => {
                        const old = p
                        return { ...old, total: newPagination.total, current: newPagination.currentPage ?? 1 }
                    })
                }
            })
    }

    const remove = (record: HouseCreativeBean) => {
        Modal.confirm({
            title: `Delete ${record.name}?`,
            content: 'Are you sure?',
            cancelText: 'NO',
            okText: 'Yes, I\'m sure',
            onOk: () => {
                houseCreativeApi.delete(record.id as number).then(() => {
                    let page = pagination.current
                    if (data.length == 1 && pagination.current > 1) {
                        page -= 1
                    }
                    loadData(page)
                })
            }
        })
    }

    useEffect(() => {
        if (searchQuery === '' && isFiltered) {
            loadData(1)
            isFiltered = false
        } else {
            debounceSearch()
        }
    }, [searchQuery])

    let isFiltered

    const debounceSearch = debounce(() => {
        loadData(1)
        isFiltered = true
    }, 1000)

    return (
        <div>
            <div style={{ display: 'flex' }}>
                <div className="c-l-search-buttons">
                    <Input
                        addonAfter={<CloseOutlined onClick={() => setSearchQuery('')} />}
                        placeholder="HouseCreative search"
                        value={searchQuery}
                        onChange={e => {
                            setSearchQuery(e.target.value), () => debounceSearch() //
                        }}
                    />
                </div>
            </div>
            <Table
                rowKey={'id'}
                size={'middle'}
                pagination={{
                    ...pagination,
                    onChange: (page: number) => loadData(page)
                }}
                dataSource={data}>
                <Column key="id" title="ID" dataIndex="id" />
                <Column key="name" title="Name" dataIndex="name" filterMultiple
                    filters={getFilterData(data, bean => bean.name)}
                    onFilter={filterFunc(bean => bean.name)}
                    sorter={sorterFunc(bean => bean.name)}
                    render={text => searchQuery !== '' ?
                        <Highlighter
                            highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
                            searchWords={[searchQuery]}
                            autoEscape
                            textToHighlight={text.toString()} /> :
                        text}
                />
                <Column key="format" title="Format" dataIndex="format"/>
                <Column align={'center'} key="action" title="Actions" render={(text: string, record: HouseCreativeBean) => (
                    <span>
                        <Divider className="actions-horisontable-divider"
                            type={areActionsVertical ? 'horizontal' : 'vertical'} />
                        <Link to={`/house-creative/${record.id}/edit`}>
                            <Button htmlType="button" title="Edit">
                                <EditOutlined />
                            </Button>
                        </Link>
                        <Divider className="actions-horisontable-divider"
                            type={areActionsVertical ? 'horizontal' : 'vertical'} />
                        <Button
                            onClick={() => remove(record)}
                            type="primary"
                            htmlType="button"
                            title="Delete"
                            danger>
                            <DeleteOutlined />
                        </Button>
                    </span>
                )} />
            </Table>
            <Link to={`/house-creative/create`}>
                <Button htmlType="button" type="primary">Create new</Button>
            </Link>
        </div>
    )
}

export default withActionsMediaQuery('550px')(HouseCreativeList)
